import React, { useState, useEffect, useCallback } from 'react';
import {
  FaShieldAlt, FaDatabase, FaClock, FaPaperPlane, FaCheckCircle,
  FaTimesCircle, FaSync, FaEye, FaDownload, FaEnvelope,
  FaCalendarAlt, FaHistory, FaPlay, FaPause, FaCog,
  FaUsers, FaSearch, FaCheckSquare, FaRegSquare,
  FaChevronLeft, FaChevronRight, FaBuilding, FaPhone
} from 'react-icons/fa';
import PageLayout from '../../components/ui/PageLayout';
import PageHeader from '../../components/ui/PageHeader';
import StatCard, { StatGrid } from '../../components/ui/StatCard';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import { useToast } from '../../context/ToastContext';
import ExportUserModal from '../Client/ExportUserModal';

const BackupSchedulePage = () => {
  const { success: toastSuccess, error: toastError } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingNow, setSendingNow] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Backup Schedule Configuration State
  const [config, setConfig] = useState({
    recipientEmail: 'contact@bookscomply.com',
    frequency: '15-days',
    intervalDays: 15,
    isActive: true,
    lastSentAt: null,
    lastStatus: 'pending',
    nextScheduledAt: null,
    reminderLogs: []
  });

  const [platformStats, setPlatformStats] = useState({
    totalUsers: 0,
    totalPayments: 0,
    totalAnnualReports: 0
  });

  const [emailPreviewHtml, setEmailPreviewHtml] = useState('');
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Form State
  const [formFrequency, setFormFrequency] = useState('15-days');
  const [formIntervalDays, setFormIntervalDays] = useState(15);
  const [formRecipientEmail, setFormRecipientEmail] = useState('contact@bookscomply.com');
  const [formIsActive, setFormIsActive] = useState(true);

  // Users List & Selection State for Data Export
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);

  // Export User Modal State
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportUser, setExportUser] = useState(null);
  const [isAllExport, setIsAllExport] = useState(false);
  const [modalSelectedUserIds, setModalSelectedUserIds] = useState([]);

  const token = localStorage.getItem('adminToken');
  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

  // Fetch Backup Schedule Settings and Stats
  const fetchBackupSchedule = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/auth/backup-schedule`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setConfig(data.config);
        setPlatformStats(data.stats || {});
        setFormFrequency(data.config.frequency || '15-days');
        setFormIntervalDays(data.config.intervalDays || 15);
        setFormRecipientEmail(data.config.recipientEmail || 'contact@bookscomply.com');
        setFormIsActive(data.config.isActive ?? true);
      } else {
        toastError(data.message || 'Failed to fetch backup schedule settings');
      }
    } catch (err) {
      console.error('Error fetching backup schedule:', err);
      toastError('Failed to load backup schedule');
    } finally {
      setLoading(false);
    }
  }, [API_BASE, token, toastError]);

  // Fetch Live Email Preview
  const fetchEmailPreview = useCallback(async () => {
    try {
      setLoadingPreview(true);
      const res = await fetch(`${API_BASE}/api/auth/backup-schedule/preview`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setEmailPreviewHtml(data.html);
      }
    } catch (err) {
      console.error('Error fetching email preview:', err);
    } finally {
      setLoadingPreview(false);
    }
  }, [API_BASE, token]);

  // Fetch Platform Users for Selection & Export
  const fetchUsers = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const res = await fetch(`${API_BASE}/api/auth/users`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (!res.ok) throw new Error('Failed to fetch clients');
      const data = await res.json();
      if (Array.isArray(data)) {
        setUsers(data);
      } else if (data.users && Array.isArray(data.users)) {
        setUsers(data.users);
      }
    } catch (err) {
      console.error('Error fetching users for backup page:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, [API_BASE, token]);

  useEffect(() => {
    fetchBackupSchedule();
    fetchEmailPreview();
    fetchUsers();
  }, [fetchBackupSchedule, fetchEmailPreview, fetchUsers]);

  // Handle Frequency Change
  const handleFrequencyChange = (val) => {
    setFormFrequency(val);
    if (val === '7-days') setFormIntervalDays(7);
    else if (val === '15-days') setFormIntervalDays(15);
    else if (val === '30-days' || val === 'monthly') setFormIntervalDays(30);
  };

  // Save Schedule Settings
  const handleSaveSettings = async (e) => {
    if (e) e.preventDefault();
    try {
      setSaving(true);
      const res = await fetch(`${API_BASE}/api/auth/backup-schedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          frequency: formFrequency,
          intervalDays: Number(formIntervalDays),
          recipientEmail: formRecipientEmail,
          isActive: formIsActive
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        toastSuccess('Backup schedule updated successfully!');
        setConfig(data.config);
        fetchEmailPreview();
      } else {
        toastError(data.message || 'Failed to update schedule');
      }
    } catch (err) {
      console.error('Error saving schedule:', err);
      toastError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  // Toggle Active/Paused state directly
  const handleToggleActive = async () => {
    const newStatus = !config.isActive;
    try {
      const res = await fetch(`${API_BASE}/api/auth/backup-schedule`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          isActive: newStatus
        })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setConfig(data.config);
        setFormIsActive(newStatus);
        toastSuccess(`Automated backup reminder ${newStatus ? 'Resumed' : 'Paused'}!`);
      }
    } catch (err) {
      toastError('Failed to toggle status');
    }
  };

  // Send Reminder Email Immediately
  const handleSendReminderNow = async () => {
    if (!window.confirm(`Send backup reminder email now to ${config.recipientEmail || 'contact@bookscomply.com'}?`)) {
      return;
    }
    try {
      setSendingNow(true);
      const res = await fetch(`${API_BASE}/api/auth/backup-schedule/send-now`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: formRecipientEmail
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        toastSuccess(`Backup reminder email sent to ${data.recipient}!`);
        fetchBackupSchedule();
        fetchEmailPreview();
      } else {
        toastError(data.message || 'Failed to send reminder email');
      }
    } catch (err) {
      console.error('Error sending reminder:', err);
      toastError('Failed to send reminder email');
    } finally {
      setSendingNow(false);
    }
  };

  // Export Handlers
  const handleExportAllPlatformData = () => {
    setExportUser(null);
    setIsAllExport(true);
    setModalSelectedUserIds([]);
    setShowExportModal(true);
  };

  const handleExportSelectedClients = () => {
    if (selectedUserIds.length === 0) {
      toastError('Please select at least one client to export');
      return;
    }
    setExportUser(null);
    setIsAllExport(true);
    setModalSelectedUserIds(selectedUserIds);
    setShowExportModal(true);
  };

  const handleExportSingleUser = (user) => {
    setExportUser(user);
    setIsAllExport(false);
    setModalSelectedUserIds([]);
    setShowExportModal(true);
  };

  // User Selection Handlers
  const handleToggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleToggleSelectAllPage = () => {
    const currentPageIds = currentPaginatedUsers.map((u) => u._id);
    const allCurrentSelected = currentPageIds.length > 0 && currentPageIds.every((id) => selectedUserIds.includes(id));
    if (allCurrentSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !currentPageIds.includes(id)));
    } else {
      setSelectedUserIds((prev) => Array.from(new Set([...prev, ...currentPageIds])));
    }
  };

  const handleSelectAllFiltered = () => {
    const allFilteredIds = filteredUsers.map((u) => u._id);
    setSelectedUserIds(allFilteredIds);
  };

  const handleClearSelection = () => {
    setSelectedUserIds([]);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Days remaining until next scheduled backup
  const getDaysRemaining = (nextDate) => {
    if (!nextDate) return null;
    const now = new Date();
    const due = new Date(nextDate);
    const diffMs = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining(config.nextScheduledAt);

  // Client Filtering & Pagination
  const filteredUsers = users.filter((u) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const name = (u.name || '').toLowerCase();
    const email = (u.email || '').toLowerCase();
    const org = (u.organization || '').toLowerCase();
    const phone = (u.phone || '').toLowerCase();
    return name.includes(q) || email.includes(q) || org.includes(q) || phone.includes(q);
  });

  const clientsPerPage = 10;
  const totalPages = Math.ceil(filteredUsers.length / clientsPerPage) || 1;
  const indexOfLastClient = currentPage * clientsPerPage;
  const indexOfFirstClient = indexOfLastClient - clientsPerPage;
  const currentPaginatedUsers = filteredUsers.slice(indexOfFirstClient, indexOfLastClient);
  const isAllCurrentPageSelected =
    currentPaginatedUsers.length > 0 &&
    currentPaginatedUsers.every((u) => selectedUserIds.includes(u._id));

  return (
    <PageLayout>
      {/* Header */}
      <PageHeader
        title="User Data Backup & Reminder Scheduler"
        description="Configure automated 15-day backup reminders, export complete platform data archives, or select individual client records to export."
        icon={FaShieldAlt}
        actions={
          <div className="flex items-center gap-2.5 flex-wrap">
            {/* Export All Platform Data Button */}
            <button
              onClick={handleExportAllPlatformData}
              className="flex items-center gap-2 px-3.5 py-2 text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 rounded-xl shadow-md shadow-indigo-500/25 transition-all cursor-pointer"
            >
              <FaDownload />
              <span>Export All Platform Data</span>
            </button>

            <button
              onClick={() => setShowPreviewModal(true)}
              className="flex items-center gap-2 px-3.5 py-2 text-xs font-semibold text-gray-700 dark:text-gray-200 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-600 transition-all shadow-xs cursor-pointer"
            >
              <FaEye className="text-primary-500" />
              <span>Email Preview</span>
            </button>

            <button
              onClick={handleSendReminderNow}
              disabled={sendingNow}
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 rounded-xl shadow-md shadow-primary-500/25 transition-all cursor-pointer"
            >
              {sendingNow ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <FaPaperPlane />
                  <span>Send Reminder Now</span>
                </>
              )}
            </button>
          </div>
        }
      />

      {loading ? (
        <SkeletonLoader variant="cards" count={5} />
      ) : (
        <>
          {/* Redesigned Executive Stat Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
            {/* Card 1: Automated Status */}
            <div className="relative group bg-white dark:bg-dark-800 rounded-2xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all duration-300 border-t-4 border-t-emerald-500 overflow-hidden flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200/60 dark:border-emerald-800/50 flex items-center justify-center text-emerald-600 dark:text-emerald-400 group-hover:scale-105 transition-transform shadow-xs">
                    {config.isActive ? <FaCheckCircle size={18} /> : <FaPause size={16} />}
                  </div>
                  <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                    config.isActive
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/60'
                      : 'bg-amber-50 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 border border-amber-200 dark:border-amber-800/60'
                  }`}>
                    {config.isActive && <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />}
                    {config.isActive ? 'Running' : 'Paused'}
                  </span>
                </div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-400">
                  Automated Status
                </p>
                <h4 className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-1 tracking-tight">
                  {config.isActive ? 'Active' : 'Paused'}
                </h4>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 truncate">
                {config.isActive ? 'Background cron running' : 'Reminders currently stopped'}
              </p>
            </div>

            {/* Card 2: Reminder Interval */}
            <div className="relative group bg-white dark:bg-dark-800 rounded-2xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all duration-300 border-t-4 border-t-blue-500 overflow-hidden flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-200/60 dark:border-blue-800/50 flex items-center justify-center text-blue-600 dark:text-blue-400 group-hover:scale-105 transition-transform shadow-xs">
                    <FaClock size={18} />
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-blue-50 text-blue-700 dark:bg-blue-950/50 dark:text-blue-300 border border-blue-200 dark:border-blue-800/60">
                    {config.intervalDays === 15 ? 'Bi-Weekly' : config.intervalDays === 7 ? 'Weekly' : config.intervalDays === 30 ? 'Monthly' : `${config.intervalDays}d Cycle`}
                  </span>
                </div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-400">
                  Reminder Interval
                </p>
                <h4 className="text-xl font-black text-gray-900 dark:text-white mt-1 tracking-tight">
                  Every {config.intervalDays || 15} Days
                </h4>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 truncate">
                Dispatched automatically
              </p>
            </div>

            {/* Card 3: Next Scheduled Due */}
            <div className="relative group bg-white dark:bg-dark-800 rounded-2xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all duration-300 border-t-4 border-t-purple-500 overflow-hidden flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-950/40 border border-purple-200/60 dark:border-purple-800/50 flex items-center justify-center text-purple-600 dark:text-purple-400 group-hover:scale-105 transition-transform shadow-xs">
                    <FaCalendarAlt size={18} />
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-purple-50 text-purple-700 dark:bg-purple-950/50 dark:text-purple-300 border border-purple-200 dark:border-purple-800/60">
                    {daysRemaining !== null ? (daysRemaining <= 0 ? 'Due Today' : `${daysRemaining}d Left`) : 'Idle'}
                  </span>
                </div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-400">
                  Next Scheduled Run
                </p>
                <h4 className="text-xl font-black text-gray-900 dark:text-white mt-1 tracking-tight">
                  {daysRemaining !== null
                    ? daysRemaining <= 0
                      ? 'Due Today'
                      : `In ${daysRemaining} day${daysRemaining > 1 ? 's' : ''}`
                    : 'Not Scheduled'}
                </h4>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 truncate">
                {config.nextScheduledAt ? formatDate(config.nextScheduledAt).split(',')[0] : 'Set in scheduler'}
              </p>
            </div>

            {/* Card 4: Platform Clients */}
            <div className="relative group bg-white dark:bg-dark-800 rounded-2xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all duration-300 border-t-4 border-t-indigo-500 overflow-hidden flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-indigo-50 dark:bg-indigo-950/40 border border-indigo-200/60 dark:border-indigo-800/50 flex items-center justify-center text-indigo-600 dark:text-indigo-400 group-hover:scale-105 transition-transform shadow-xs">
                    <FaUsers size={18} />
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800/60">
                    Database
                  </span>
                </div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-400">
                  Platform Clients
                </p>
                <h4 className="text-xl font-black text-gray-900 dark:text-white mt-1 tracking-tight">
                  {users.length || platformStats.totalUsers || 0} Registered
                </h4>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 truncate">
                Ready for data backup & export
              </p>
            </div>

            {/* Card 5: Last Reminder Sent */}
            <div className="relative group bg-white dark:bg-dark-800 rounded-2xl p-5 border border-gray-100 dark:border-dark-700 shadow-sm hover:shadow-md transition-all duration-300 border-t-4 border-t-orange-500 overflow-hidden flex flex-col justify-between">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-950/40 border border-orange-200/60 dark:border-orange-800/50 flex items-center justify-center text-orange-600 dark:text-orange-400 group-hover:scale-105 transition-transform shadow-xs">
                    <FaHistory size={18} />
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase tracking-wider bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300 border border-orange-200 dark:border-orange-800/60">
                    {config.reminderLogs?.length || 0} Logged
                  </span>
                </div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-400">
                  Last Reminder Sent
                </p>
                <h4 className="text-xl font-black text-gray-900 dark:text-white mt-1 tracking-tight truncate">
                  {config.lastSentAt ? formatDate(config.lastSentAt).split(',')[0] : 'Never Sent'}
                </h4>
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2 truncate">
                {config.lastSentAt ? (config.lastStatus === 'sent' ? 'Delivered successfully' : 'Status: ' + config.lastStatus) : 'No reminder dispatched yet'}
              </p>
            </div>
          </div>

          {/* Quick Platform Backup Banner */}
          <div className="bg-gradient-to-r from-purple-900/90 via-indigo-900/90 to-slate-900/90 rounded-2xl p-6 text-white shadow-lg border border-purple-700/40 mb-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center flex-shrink-0 border border-white/20 shadow-inner">
                <FaDatabase className="text-purple-300 text-2xl" />
              </div>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base font-bold text-white">Full Platform Data Backup Archive</h3>
                  <span className="px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase bg-purple-500/30 text-purple-200 border border-purple-400/30">
                    Complete .ZIP
                  </span>
                </div>
                <p className="text-xs text-purple-200/80 mt-1 max-w-2xl leading-relaxed">
                  Export all platform records including all client profiles, company formation stepfiles, annual filings, invoices, payments, chat messages, and system settings into a single downloadable .ZIP archive.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={handleExportAllPlatformData}
              className="flex items-center gap-2.5 px-5 py-3 text-xs font-bold text-white bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 rounded-xl shadow-lg shadow-purple-900/50 hover:shadow-purple-900/70 transition-all cursor-pointer flex-shrink-0"
            >
              <FaDownload className="text-sm" />
              <span>Export All Platform Data</span>
            </button>
          </div>

          {/* Main Content: Configuration + Live Email Preview */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-8">
            {/* Left: Schedule Configuration Card */}
            <div className="lg:col-span-6 bg-white dark:bg-dark-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-dark-700">
              <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-dark-700 mb-6">
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
                    <FaCog className="text-primary-500" /> Schedule Configuration
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Select how frequently the backup reminder email should be dispatched.
                  </p>
                </div>

                {/* Quick Pause / Resume Switch */}
                <button
                  type="button"
                  onClick={handleToggleActive}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    config.isActive
                      ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800/40 hover:bg-amber-100'
                      : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/40 hover:bg-emerald-100'
                  }`}
                >
                  {config.isActive ? <FaPause size={10} /> : <FaPlay size={10} />}
                  <span>{config.isActive ? 'Pause Scheduler' : 'Resume Scheduler'}</span>
                </button>
              </div>

              <form onSubmit={handleSaveSettings} className="space-y-5">
                {/* Frequency Select */}
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 dark:text-gray-300 mb-2">
                    Backup Frequency (Cycle)
                  </label>
                  <div className="grid grid-cols-2 gap-2.5">
                    {[
                      { id: '7-days', label: 'Every 7 Days', sub: 'Weekly cycle' },
                      { id: '15-days', label: 'Every 15 Days', sub: 'Bi-Weekly (Recommended)' },
                      { id: '30-days', label: 'Every 30 Days', sub: 'Monthly cycle' },
                      { id: 'custom', label: 'Custom Days', sub: 'Specify days interval' }
                    ].map((opt) => (
                      <button
                        type="button"
                        key={opt.id}
                        onClick={() => handleFrequencyChange(opt.id)}
                        className={`p-3 rounded-xl border text-left transition-all cursor-pointer ${
                          formFrequency === opt.id
                            ? 'bg-primary-50 dark:bg-primary-950/40 border-primary-500 ring-2 ring-primary-500/20 text-primary-900 dark:text-primary-100'
                            : 'bg-gray-50/70 dark:bg-dark-900/40 border-gray-200 dark:border-dark-700 text-gray-700 dark:text-gray-300 hover:border-gray-300'
                        }`}
                      >
                        <p className="text-xs font-bold">{opt.label}</p>
                        <p className="text-[11px] text-gray-400 mt-0.5">{opt.sub}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Days Interval */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5">
                    Interval Days (Days between reminders)
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={formIntervalDays}
                      onChange={(e) => {
                        setFormIntervalDays(e.target.value);
                        if (formFrequency !== 'custom') setFormFrequency('custom');
                      }}
                      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-xl text-sm font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                      placeholder="e.g. 15"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-semibold text-gray-400 pointer-events-none">
                      Days
                    </span>
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">
                    The automated cron will remind the admin team every <strong>{formIntervalDays}</strong> days.
                  </p>
                </div>

                {/* Recipient Email */}
                <div>
                  <label className="block text-xs font-bold text-gray-700 dark:text-gray-300 mb-1.5 flex items-center justify-between">
                    <span>Recipient Email Address</span>
                    <span className="text-[11px] text-primary-500 font-medium">Default: contact@bookscomply.com</span>
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                    <input
                      type="email"
                      required
                      value={formRecipientEmail}
                      onChange={(e) => setFormRecipientEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-xl text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 font-mono"
                      placeholder="contact@bookscomply.com"
                    />
                  </div>
                  <p className="text-[11px] text-gray-400 mt-1">
                    Backup alerts and platform statistics will be delivered to this inbox.
                  </p>
                </div>

                {/* Active Checkbox */}
                <div className="p-3.5 bg-gray-50 dark:bg-dark-900/50 rounded-xl border border-gray-100 dark:border-dark-700 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold text-gray-900 dark:text-white">Enable Automated Reminders</p>
                    <p className="text-[11px] text-gray-400">Keep cron background job running automatically</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={formIsActive}
                    onChange={(e) => setFormIsActive(e.target.checked)}
                    className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500 cursor-pointer"
                  />
                </div>

                {/* Save Button */}
                <div className="pt-3 flex items-center gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white text-sm font-bold rounded-xl shadow-md shadow-primary-500/30 transition-all cursor-pointer flex items-center justify-center gap-2"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Saving Changes...</span>
                      </>
                    ) : (
                      <>
                        <FaShieldAlt />
                        <span>Save Schedule Settings</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* Right: Live Email Preview Container */}
            <div className="lg:col-span-6 bg-white dark:bg-dark-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-dark-700 flex flex-col">
              <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-dark-700 mb-4">
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
                    <FaEye className="text-primary-500" /> Live Email Preview
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Exact template delivered to <strong>{config.recipientEmail || 'contact@bookscomply.com'}</strong>
                  </p>
                </div>

                <button
                  type="button"
                  onClick={fetchEmailPreview}
                  disabled={loadingPreview}
                  className="p-2 text-gray-400 hover:text-primary-500 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-700 transition-colors cursor-pointer"
                  title="Refresh Preview"
                >
                  <FaSync className={`w-3.5 h-3.5 ${loadingPreview ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {/* Email Mock Container */}
              <div className="flex-1 bg-gray-100 dark:bg-dark-900/60 rounded-xl p-4 overflow-y-auto max-h-[500px] border border-gray-200 dark:border-dark-700">
                {emailPreviewHtml ? (
                  <div
                    className="bg-white rounded-xl shadow-xs overflow-hidden"
                    dangerouslySetInnerHTML={{ __html: emailPreviewHtml }}
                  />
                ) : (
                  <div className="p-8 text-center text-gray-400">
                    <FaEnvelope className="w-8 h-8 mx-auto mb-2 opacity-40" />
                    <p className="text-xs">Generating live email preview...</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Client Selection & Data Export Section */}
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden mb-8">
            {/* Header & Controls */}
            <div className="p-6 border-b border-gray-100 dark:border-dark-700">
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2.5">
                    <h3 className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
                      <FaUsers className="text-primary-500" /> Client Data Selection & Export
                    </h3>
                    <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300 border border-primary-200 dark:border-primary-800/40">
                      {filteredUsers.length} Clients
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Select individual or multiple clients below to export their complete data archives (.ZIP), or export single records directly.
                  </p>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                  {/* Search Input */}
                  <div className="relative min-w-[260px] flex-1 sm:flex-initial">
                    <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Search client name, email, company..."
                      className="w-full pl-9 pr-7 py-2 bg-gray-50 dark:bg-dark-900 border border-gray-200 dark:border-dark-700 rounded-xl text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => { setSearchQuery(''); setCurrentPage(1); }}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs p-1"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {/* Export All Platform Data Button */}
                  <button
                    type="button"
                    onClick={handleExportAllPlatformData}
                    className="flex items-center gap-2 px-3.5 py-2 text-xs font-bold text-white bg-purple-600 hover:bg-purple-700 rounded-xl shadow-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    <FaDownload />
                    <span>Export All Platform Data</span>
                  </button>
                </div>
              </div>

              {/* Selection Bar / Bulk Actions */}
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-dark-700/60 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="flex items-center gap-2 text-xs font-semibold text-gray-700 dark:text-gray-300 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={isAllCurrentPageSelected}
                      onChange={handleToggleSelectAllPage}
                      className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer"
                    />
                    <span>Select All on Page ({currentPaginatedUsers.length})</span>
                  </label>

                  {filteredUsers.length > currentPaginatedUsers.length && (
                    <button
                      type="button"
                      onClick={handleSelectAllFiltered}
                      className="text-xs text-primary-600 dark:text-primary-400 font-semibold hover:underline cursor-pointer"
                    >
                      Select All Filtered ({filteredUsers.length})
                    </button>
                  )}

                  {selectedUserIds.length > 0 && (
                    <span className="text-xs font-bold text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-950/40 px-2.5 py-1 rounded-full border border-primary-200 dark:border-primary-800/40">
                      {selectedUserIds.length} Selected
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  {selectedUserIds.length > 0 ? (
                    <>
                      <button
                        type="button"
                        onClick={handleClearSelection}
                        className="px-3 py-1.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-700 rounded-lg transition-colors cursor-pointer"
                      >
                        Clear Selection
                      </button>

                      <button
                        type="button"
                        onClick={handleExportSelectedClients}
                        className="flex items-center gap-2 px-4 py-1.5 text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl shadow-md shadow-indigo-500/20 transition-all cursor-pointer animate-in fade-in"
                      >
                        <FaDownload />
                        <span>Export Selected ({selectedUserIds.length}) Clients</span>
                      </button>
                    </>
                  ) : (
                    <span className="text-xs text-gray-400 italic">
                      Check boxes to select multiple clients for bulk export
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Table / Loading State */}
            {loadingUsers ? (
              <div className="p-8">
                <SkeletonLoader variant="table" count={5} />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="p-12 text-center text-gray-400 dark:text-gray-500">
                <FaUsers className="w-10 h-10 mx-auto mb-2 opacity-30" />
                <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                  {searchQuery ? "No clients match your search criteria" : "No clients found on the platform"}
                </p>
                {searchQuery && (
                  <button
                    onClick={() => { setSearchQuery(""); setCurrentPage(1); }}
                    className="mt-2 text-xs text-primary-600 dark:text-primary-400 font-medium hover:underline cursor-pointer"
                  >
                    Clear search query
                  </button>
                )}
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-dark-900/50 text-gray-500 dark:text-gray-400 uppercase tracking-wider font-bold border-b border-gray-100 dark:border-dark-700">
                        <th className="py-3 px-4 w-12 text-center">
                          <input
                            type="checkbox"
                            checked={isAllCurrentPageSelected}
                            onChange={handleToggleSelectAllPage}
                            className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer"
                            title="Select all on this page"
                          />
                        </th>
                        <th className="py-3 px-4">Client / Organization</th>
                        <th className="py-3 px-4">Contact Info</th>
                        <th className="py-3 px-4">Selected Services</th>
                        <th className="py-3 px-4">Registered Date</th>
                        <th className="py-3 px-4 text-right">Export Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                      {currentPaginatedUsers.map((user) => {
                        const isSelected = selectedUserIds.includes(user._id);
                        return (
                          <tr
                            key={user._id}
                            className={`transition-colors ${
                              isSelected
                                ? 'bg-primary-50/40 dark:bg-primary-950/20'
                                : 'hover:bg-gray-50/50 dark:hover:bg-dark-800/40'
                            }`}
                          >
                            {/* Checkbox */}
                            <td className="py-3.5 px-4 text-center">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleToggleUserSelection(user._id)}
                                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 cursor-pointer"
                              />
                            </td>

                            {/* Client / Organization */}
                            <td className="py-3.5 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-primary-600 to-indigo-600 text-white font-bold text-xs flex items-center justify-center flex-shrink-0 shadow-xs">
                                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                                </div>
                                <div className="min-w-0">
                                  <p className="font-bold text-gray-900 dark:text-white truncate">
                                    {user.name || 'Unnamed Client'}
                                  </p>
                                  {user.organization ? (
                                    <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1 truncate mt-0.5">
                                      <FaBuilding size={10} className="text-gray-400" />
                                      {user.organization}
                                    </p>
                                  ) : (
                                    <span className="text-[10px] text-gray-400">Individual Account</span>
                                  )}
                                </div>
                              </div>
                            </td>

                            {/* Contact Info */}
                            <td className="py-3.5 px-4">
                              <div className="space-y-0.5">
                                <p className="font-mono text-gray-700 dark:text-gray-300 truncate">
                                  {user.email || '—'}
                                </p>
                                {user.phone && (
                                  <p className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                    <FaPhone size={9} className="text-gray-400" />
                                    {user.phone}
                                  </p>
                                )}
                              </div>
                            </td>

                            {/* Selected Services */}
                            <td className="py-3.5 px-4">
                              <div className="flex flex-wrap gap-1 max-w-xs">
                                {user.selectedServices && user.selectedServices.length > 0 ? (
                                  user.selectedServices.slice(0, 3).map((srv, idx) => (
                                    <span
                                      key={idx}
                                      className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-gray-100 text-gray-700 dark:bg-dark-700 dark:text-gray-300"
                                    >
                                      {typeof srv === 'string' ? srv : srv?.title || 'Service'}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-gray-400 text-[11px]">No services selected</span>
                                )}
                                {user.selectedServices && user.selectedServices.length > 3 && (
                                  <span className="px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-primary-50 text-primary-600 dark:bg-primary-950/40 dark:text-primary-400">
                                    +{user.selectedServices.length - 3} more
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Registered Date */}
                            <td className="py-3.5 px-4 text-gray-600 dark:text-gray-400">
                              {user.createdAt ? formatDate(user.createdAt).split(',')[0] : '—'}
                            </td>

                            {/* Actions */}
                            <td className="py-3.5 px-4 text-right">
                              <button
                                type="button"
                                onClick={() => handleExportSingleUser(user)}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-950/40 border border-primary-200 dark:border-primary-800/50 hover:bg-primary-100 dark:hover:bg-primary-900/40 transition-all cursor-pointer"
                                title={`Export data archive for ${user.name || 'this client'}`}
                              >
                                <FaDownload size={11} />
                                <span>Export Data</span>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-100 dark:border-dark-700 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-gray-500">
                    <div>
                      Showing <span className="font-semibold text-gray-800 dark:text-gray-200">{indexOfFirstClient + 1}</span> to{' '}
                      <span className="font-semibold text-gray-800 dark:text-gray-200">
                        {Math.min(indexOfLastClient, filteredUsers.length)}
                      </span>{' '}
                      of <span className="font-semibold text-gray-800 dark:text-gray-200">{filteredUsers.length}</span> clients
                    </div>

                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="p-2 border border-gray-200 dark:border-dark-700 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-40 transition-colors cursor-pointer"
                      >
                        <FaChevronLeft size={10} />
                      </button>

                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                        .map((page, idx, arr) => {
                          const prev = arr[idx - 1];
                          const showEllipsis = prev && page - prev > 1;
                          return (
                            <React.Fragment key={page}>
                              {showEllipsis && <span className="px-1 text-gray-400">…</span>}
                              <button
                                onClick={() => setCurrentPage(page)}
                                className={`w-7 h-7 rounded-lg font-bold transition-colors cursor-pointer ${
                                  currentPage === page
                                    ? 'bg-primary-600 text-white'
                                    : 'border border-gray-200 dark:border-dark-700 hover:bg-gray-50 dark:hover:bg-dark-700 text-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        })}

                      <button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="p-2 border border-gray-200 dark:border-dark-700 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 disabled:opacity-40 transition-colors cursor-pointer"
                      >
                        <FaChevronRight size={10} />
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Delivery History & Logs Table */}
          <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-700 overflow-hidden mb-6">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-dark-700 flex items-center justify-between">
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white text-base flex items-center gap-2">
                  <FaHistory className="text-primary-500" /> Reminder Delivery History
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  Logs of automated and manual backup reminder emails sent to admin
                </p>
              </div>
              <span className="px-2.5 py-1 text-xs font-bold bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300 rounded-full">
                {config.reminderLogs?.length || 0} Total Dispatches
              </span>
            </div>

            {!config.reminderLogs || config.reminderLogs.length === 0 ? (
              <div className="p-8 text-center text-gray-400 dark:text-gray-500">
                <FaDatabase className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs font-semibold">No backup reminder emails sent yet.</p>
                <p className="text-[11px] text-gray-400 mt-1">
                  Click &ldquo;Send Reminder Now&rdquo; above to dispatch the first reminder email.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs border-collapse">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-dark-900/50 text-gray-500 dark:text-gray-400 uppercase tracking-wider font-bold border-b border-gray-100 dark:border-dark-700">
                      <th className="py-3 px-4">Sent At (Date & Time)</th>
                      <th className="py-3 px-4">Recipient</th>
                      <th className="py-3 px-4">Trigger Type</th>
                      <th className="py-3 px-4">Delivery Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                    {config.reminderLogs.map((log, index) => (
                      <tr key={index} className="hover:bg-gray-50/50 dark:hover:bg-dark-800/30">
                        <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                          <FaClock className="text-gray-400" size={12} />
                          {formatDate(log.sentAt)}
                        </td>
                        <td className="py-3 px-4 font-mono text-gray-600 dark:text-gray-300">
                          {log.recipient}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider ${
                            log.triggerType === 'manual'
                              ? 'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800'
                              : 'bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800'
                          }`}>
                            {log.triggerType === 'manual' ? 'Manual Admin' : 'Automated Cron'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {log.status === 'sent' ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                              <FaCheckCircle size={10} /> Delivered
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300 border border-red-200 dark:border-red-800">
                              <FaTimesCircle size={10} /> Failed
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* Export User Data & Platform Modal */}
      <ExportUserModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        user={exportUser}
        isAllExport={isAllExport}
        selectedUserIds={modalSelectedUserIds}
      />

      {/* Email Preview Modal */}
      {showPreviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
          <div className="relative bg-white dark:bg-dark-800 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-gray-200 dark:border-dark-700">
            <div className="px-6 py-4 bg-sidebar-gradient text-white flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold flex items-center gap-2">
                  <FaShieldAlt /> Backup Reminder Email Preview
                </h3>
                <p className="text-xs text-secondary-300 mt-0.5">
                  Delivered to: {config.recipientEmail || 'contact@bookscomply.com'}
                </p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-xl transition-all cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 p-6 overflow-y-auto bg-gray-50 dark:bg-dark-900/60">
              {emailPreviewHtml ? (
                <div
                  className="bg-white rounded-xl shadow-xs overflow-hidden"
                  dangerouslySetInnerHTML={{ __html: emailPreviewHtml }}
                />
              ) : (
                <p className="text-center text-gray-400 py-10">Loading preview...</p>
              )}
            </div>

            <div className="p-4 bg-gray-50 dark:bg-dark-800 border-t border-gray-100 dark:border-dark-700 flex justify-end gap-3">
              <button
                onClick={() => setShowPreviewModal(false)}
                className="px-4 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-dark-700 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-gray-100 dark:hover:bg-dark-600 transition-all cursor-pointer"
              >
                Close Preview
              </button>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  handleSendReminderNow();
                }}
                className="px-4 py-2 text-xs font-bold text-white bg-primary-600 hover:bg-primary-700 rounded-xl shadow-md transition-all cursor-pointer flex items-center gap-1.5"
              >
                <FaPaperPlane size={10} /> Send This Email Now
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default BackupSchedulePage;
